import React, {useState, useEffect, useCallback} from 'react';
import {Form, Input, Button, Upload, Row, Col, Typography, Modal, Space, message, Image, Alert} from 'antd';
import {UploadOutlined, CheckCircleOutlined, CloseCircleOutlined, ArrowLeftOutlined} from '@ant-design/icons';
import {motion} from 'framer-motion';
import {useMediaQuery} from 'react-responsive';
import {useSelector} from 'react-redux';
import PaymentInformationService from '../services/PaymentInformationService';
import theme from "../theme";
import config from '../config/appConfig';
import promoterService from "../services/PromoterService";
import {useNavigate} from "react-router-dom";

const {Title, Text} = Typography;

const PaymentInformationForm = ({electorKey = null, fullName = null}) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState({
        apiFileTaxCertificate: [],
        apiFileCurp: [],
        apiFileElectorKey: [],
        apiFileBankStatementCover: [],
    });
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [modalMessage, setModalMessage] = useState('');
    const [existingRecord, setExistingRecord] = useState(null);
    const [previewImage, setPreviewImage] = useState('');
    const [previewVisible, setPreviewVisible] = useState(false);
    const isMobile = useMediaQuery({maxWidth: 800});
    const [messageApi, contextHolder] = message.useMessage();
    const auth = useSelector((state) => state.auth || {});
    const apiUrl = config.apiHost + config.apiPath;
    const disabledForm = existingRecord && existingRecord.status === 'APPROVED';
    const realUsername = electorKey || auth.user?.username;
    const [promoter, setPromoter] = useState(null);
    const navigate = useNavigate();

    const fetchPromoter = useCallback(async () => {
        if (electorKey) {
            try {
                const response = await promoterService.getAll();
                const found = response.data.find((p) => p.electorKey === electorKey);
                setPromoter(found || null);
            } catch (error) {
                console.error("Error al buscar promotor:", error);
            }
        }
    }, [electorKey]);

    const fetchExistingRecord = useCallback(async () => {
        try {
            const filters = {user: {value: realUsername, operator: "eq"}};
            const response = await PaymentInformationService.getAll(1, 10, filters);
            const records = response.data || [];
            if (records.length > 0) {
                const record = records[0];
                setExistingRecord(record);
                form.setFieldsValue(record);
                const newFileList = {
                    apiFileTaxCertificate: record.apiFileTaxCertificate
                        ? [{
                            uid: '-1',
                            name: record.apiFileTaxCertificate.name,
                            status: 'done',
                            url: apiUrl + `/api-files/${record.apiFileTaxCertificate.name}`,
                            isOriginal: true,
                        }]
                        : [],
                    apiFileCurp: record.apiFileCurp
                        ? [{
                            uid: '-2',
                            name: record.apiFileCurp.name,
                            status: 'done',
                            url: apiUrl + `/api-files/${record.apiFileCurp.name}`,
                            isOriginal: true,
                        }]
                        : [],
                    apiFileElectorKey: record.apiFileElectorKey
                        ? [{
                            uid: '-3',
                            name: record.apiFileElectorKey.name,
                            status: 'done',
                            url: apiUrl + `/api-files/${record.apiFileElectorKey.name}`,
                            isOriginal: true,
                        }]
                        : [],
                    apiFileBankStatementCover: record.apiFileBankStatementCover
                        ? [{
                            uid: '-4',
                            name: record.apiFileBankStatementCover.name,
                            status: 'done',
                            url: apiUrl + `/api-files/${record.apiFileBankStatementCover.name}`,
                            isOriginal: true,
                        }]
                        : [],
                };
                setFileList(newFileList);
            } else {
                setExistingRecord(null);
                form.resetFields();
                setFileList({
                    apiFileTaxCertificate: [],
                    apiFileCurp: [],
                    apiFileElectorKey: [],
                    apiFileBankStatementCover: [],
                });
            }
        } catch (error) {
            console.error("Error fetching existing payment information:", error);
        }
    }, [realUsername, form, apiUrl]);

    // Cargar datos al montar el componente
    useEffect(() => {
        fetchExistingRecord().catch(console.error);
        fetchPromoter().catch(console.error);
    }, [fetchExistingRecord, fetchPromoter]);

    const handleSubmit = useCallback(async (values) => {
        setLoading(true);
        const formData = new FormData();
        formData.append('user', realUsername);
        Object.keys(values).forEach((key) => {
            if (values[key] !== undefined && !key.startsWith('apiFile')) {
                formData.append(key, values[key] == null ? '' : values[key].toString());
            }
        });

        if (existingRecord) {
            Object.keys(fileList).forEach((key) => {
                const fileEntry = fileList[key][0];
                const hadFileOriginally = existingRecord[key] !== null && existingRecord[key] !== undefined;

                if (fileList[key].length === 0 && hadFileOriginally) {
                    formData.append(key, '');
                } else if (fileEntry && fileEntry.originFileObj && !fileEntry.isOriginal) {
                    formData.append(key, fileEntry.originFileObj);
                }
            });
        } else {
            Object.keys(fileList).forEach((key) => {
                if (fileList[key].length > 0 && fileList[key][0].originFileObj) {
                    formData.append(key, fileList[key][0].originFileObj);
                }
            });
        }

        try {
            let response;
            if (existingRecord) {
                response = await PaymentInformationService.update(existingRecord.id, formData);
                showModal('success', response.apiMessage || 'Información de nómina actualizada exitosamente');
            } else {
                response = await PaymentInformationService.create(formData);
                showModal('success', response.apiMessage || 'Información de nómina creada exitosamente');
            }
            await fetchExistingRecord();
        } catch (error) {
            const errorData = error;
            const errorCode = errorData.errorCode || '';
            let errorMessage = errorCode ? errorData.message : error.message || 'Desconocido';
            if (errorCode === "VALIDATION_ERROR" || errorCode === "BAD_REQUEST") {
                const validationErrors = errorData.details;
                errorMessage = validationErrors.flatMap(errorObj => Object.values(errorObj)).join(", ");
            }
            showModal('error', `Error al ${existingRecord ? 'actualizar' : 'crear'}: ${errorMessage}`);
        }
        setLoading(false);
    }, [existingRecord, fileList, fetchExistingRecord, realUsername]);

    const showModal = (type, message) => {
        setModalType(type);
        setModalMessage(message);
        setModalVisible(true);
    };

    const handleFileChange = (key) => ({fileList: newFileList}) => {
        const updatedList = newFileList.slice(-1);
        if (updatedList.length > 0 && updatedList[0].originFileObj) {
            const file = updatedList[0].originFileObj;
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    updatedList[0].thumbUrl = e.target.result;
                    updatedList[0].url = e.target.result;
                    setFileList((prev) => ({...prev, [key]: [...updatedList]}));
                };
                reader.readAsDataURL(file);
            }
        }
        setFileList((prev) => ({
            ...prev,
            [key]: updatedList,
        }));
    };

    const handlePreview = async (file) => {
        if (file.name.toLowerCase().endsWith('.pdf')) {
            const url = file.url || URL.createObjectURL(file.originFileObj);
            window.open(url, '_blank');
        } else {
            let previewUrl = file.url;
            if (!previewUrl && file.originFileObj) {
                previewUrl = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target.result);
                    reader.readAsDataURL(file.originFileObj);
                });
            }
            setPreviewImage(previewUrl);
            setPreviewVisible(true);
        }
    };

    const uploadProps = (key) => ({
        fileList: fileList[key],
        onChange: handleFileChange(key),
        onPreview: handlePreview,
        beforeUpload: (file) => {
            const isValidType = ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type);
            const isValidSize = file.size / 1024 / 1024 < 5;
            if (!isValidType) messageApi.error('Solo se permiten archivos PDF, JPG o PNG');
            if (!isValidSize) messageApi.error('El archivo debe ser menor a 5MB');
            return isValidType && isValidSize ? false : Upload.LIST_IGNORE;
        },
        accept: '.pdf,.jpg,.png',
        listType: 'picture',
        showUploadList: {
            showPreviewIcon: true,
            showRemoveIcon: false,
        },
    });

    const validateCURP = (_, value) => {
        const curpRegex = /^[A-Z]{4}\d{6}[A-Z]{6}[A-Z0-9]{2}$/;
        return !value || curpRegex.test(value) ? Promise.resolve() : Promise.reject('El CURP debe tener 18 caracteres y seguir el formato correcto');
    };

    const validateRFC = (_, value) => {
        const rfcRegex = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;
        return !value || rfcRegex.test(value) ? Promise.resolve() : Promise.reject('El RFC debe tener entre 12 y 13 caracteres y seguir el formato correcto');
    };

    const validateZipCode = (_, value) => {
        const zipCodeRegex = /^\d{5,6}$/;
        return !value || zipCodeRegex.test(value) ? Promise.resolve() : Promise.reject('El código postal debe ser un número de 5 a 6 dígitos');
    };

    const validateAccountNumber = (_, value) => {
        const accountNumberRegex = /^\d{10,16}$/;
        return !value || accountNumberRegex.test(value) ? Promise.resolve() : Promise.reject('El número de cuenta debe tener de 10 a 16 dígitos numéricos');
    };

    const validateCLABE = (_, value) => {
        const clabeRegex = /^\d{18}$/;
        return !value || clabeRegex.test(value) ? Promise.resolve() : Promise.reject('La CLABE debe ser un número de exactamente 18 dígitos');
    };

    return (
        <>
            {contextHolder}
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "800px",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    textAlign: 'center'
                }}
            >
                {(promoter != null && promoter.fullName) && (
                    <div style={{display: "flex", justifyContent: "flex-start", marginBottom: "20px"}}>
                        <Space>
                            <Button
                                float="left"
                                type="primary"
                                danger
                                icon={<ArrowLeftOutlined/>}
                                onClick={() => navigate('/promotores')}
                            >
                                Regresar al listado de promotores
                            </Button>
                        </Space>
                    </div>
                )}

                <Title level={2}>Información de nómina</Title>

                {(promoter != null && promoter.fullName) && (
                    <Title level={2} style={{textAlign: "center"}}>
                        <p style={{color: theme.colors.primary}}>{promoter.fullName}</p>
                    </Title>
                )}

                {(existingRecord && existingRecord.status === 'APPROVED') && (
                    <Alert
                        style={{textAlign: 'left'}}
                        description="Su información ha sido aprobada."
                        type="success"
                        showIcon
                        closable
                    />
                )}

                {(existingRecord && existingRecord.status === 'CHANGES_REQUESTED') && (
                    <Alert
                        style={{textAlign: 'left'}}
                        message="Ajustes requeridos"
                        description={existingRecord.observations}
                        type="warning"
                        showIcon
                        closable
                    />
                )}

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{marginTop: "20px", textAlign: "left"}}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Nombre (como aparece en RFC)"
                                name="name"
                                rules={[{required: true, message: 'El nombre es obligatorio'}]}
                            >
                                <Input
                                    disabled={disabledForm} placeholder="Nombre"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Apellido Paterno (como aparece en RFC)"
                                name="firstLastName"
                                rules={[{required: true, message: 'El apellido paterno es obligatorio'}]}
                            >
                                <Input
                                    disabled={disabledForm} placeholder="Apellido Paterno"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Apellido Materno (como aparece en RFC)" name="secondLastName">
                                <Input
                                    disabled={disabledForm} placeholder="Apellido Materno"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="CURP"
                                name="curp"
                                rules={[
                                    {required: true, message: 'El CURP es obligatorio'},
                                    {validator: validateCURP},
                                ]}
                            >
                                <Input
                                    disabled={disabledForm} placeholder="CURP" maxLength={18}/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="RFC"
                                name="rfc"
                                rules={[
                                    {required: true, message: 'El RFC es obligatorio'},
                                    {validator: validateRFC},
                                ]}
                            >
                                <Input
                                    disabled={disabledForm} placeholder="RFC" maxLength={13}/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Calle (como aparece en RFC)"
                                name="street"
                                rules={[{required: true, message: 'La calle es obligatoria'}]}
                            >
                                <Input
                                    disabled={disabledForm} placeholder="Calle"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Form.Item
                                label="No.Ext (como en RFC)"
                                name="exteriorNumber"
                                rules={[{required: true, message: 'El número exterior es obligatorio'}]}
                            >
                                <Input
                                    disabled={disabledForm} placeholder="Número Exterior"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Form.Item label="No.Int" name="interiorNumber">
                                <Input
                                    disabled={disabledForm} placeholder="Número Interior"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Colonia (como aparece en RFC)"
                                name="neighborhood"
                                rules={[{required: true, message: 'La colonia es obligatoria'}]}
                            >
                                <Input
                                    disabled={disabledForm} placeholder="Colonia"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Código Postal (como aparece en RFC)"
                                name="zipCode"
                                rules={[
                                    {required: true, message: 'El código postal es obligatorio'},
                                    {validator: validateZipCode},
                                ]}
                            >
                                <Input
                                    disabled={disabledForm} placeholder="Código Postal" maxLength={6}/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Nombre del Banco"
                                name="bankName"
                                rules={[{required: true, message: 'El nombre del banco es obligatorio'}]}
                            >
                                <Input
                                    disabled={disabledForm} placeholder="Nombre del Banco"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Número de Cuenta"
                                name="accountNumber"
                                rules={[
                                    {required: true, message: 'El número de cuenta es obligatorio'},
                                    {validator: validateAccountNumber},
                                ]}
                            >
                                <Input
                                    disabled={disabledForm}
                                    onChange={(e) => {
                                        const sanitizedValue = e.target.value.replace(/[^0-9]/g, '');
                                        form.setFieldsValue({accountNumber: sanitizedValue});
                                    }}
                                    placeholder="Número de Cuenta"
                                    maxLength={16}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="CLABE"
                                name="clabe"
                                rules={[
                                    {required: true, message: 'La CLABE es obligatoria'},
                                    {validator: validateCLABE},
                                ]}
                            >
                                <Input
                                    disabled={disabledForm}
                                    placeholder="CLABE"
                                    onChange={(e) => {
                                        const sanitizedValue = e.target.value.replace(/[^0-9]/g, '');
                                        form.setFieldsValue({clabe: sanitizedValue});
                                    }}
                                    maxLength={18}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Certificado Fiscal">
                                <Upload {...uploadProps('apiFileTaxCertificate')}>
                                    {!disabledForm && (
                                        <Button icon={<UploadOutlined/>}>Subir archivo</Button>
                                    )}
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="CURP (Archivo)">
                                <Upload {...uploadProps('apiFileCurp')}>
                                    {!disabledForm && (
                                        <Button icon={<UploadOutlined/>}>Subir archivo</Button>
                                    )}
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Clave de Elector">
                                <Upload {...uploadProps('apiFileElectorKey')}>
                                    {!disabledForm && (
                                        <Button icon={<UploadOutlined/>}>Subir archivo</Button>
                                    )}
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item label="Portada de Estado de Cuenta">
                                <Upload {...uploadProps('apiFileBankStatementCover')}>
                                    {!disabledForm && (
                                        <Button icon={<UploadOutlined/>}>Subir archivo</Button>
                                    )}
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>

                    {!disabledForm && (
                        <Form.Item>
                            <Space style={{width: '100%', justifyContent: 'flex-end'}}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    disabled={loading}
                                >
                                    {existingRecord ? 'Actualizar' : 'Crear'}
                                </Button>
                            </Space>
                        </Form.Item>
                    )}
                </Form>

                <Modal
                    open={modalVisible}
                    footer={null}
                    closable={false}
                    centered
                    onCancel={() => setModalVisible(false)}
                >
                    {modalType === 'success' && (
                        <div style={{textAlign: 'center'}}>
                            <CheckCircleOutlined style={{color: '#52c41a', fontSize: 48}}/>
                            <Title level={3}>Operación Exitosa</Title>
                            <Text>{modalMessage}</Text>
                        </div>
                    )}
                    {modalType === 'error' && (
                        <div style={{textAlign: 'center'}}>
                            <CloseCircleOutlined style={{color: '#f5222d', fontSize: 48}}/>
                            <Title level={3}>Error</Title>
                            <Text>{modalMessage}</Text>
                        </div>
                    )}
                </Modal>

                {previewImage && (
                    <Image
                        wrapperStyle={{display: 'none'}}
                        preview={{
                            visible: previewVisible,
                            onVisibleChange: (visible) => setPreviewVisible(visible),
                            afterOpenChange: (visible) => !visible && setPreviewImage(''),
                        }}
                        src={previewImage}
                    />
                )}
            </motion.div>
        </>
    );
};

export default PaymentInformationForm;